/**
 * Mirrors of the API's identity enums (bovid-api/src/common/enums). Keep the values in sync —
 * the API is the source of truth; the admin only reads them from the access-token session.
 */

export const UserType = {
  SUPER_ADMIN: 'SUPER_ADMIN',
  ADMIN: 'ADMIN',
  CLIENT_ADMIN: 'CLIENT_ADMIN',
  FIELD_ADMIN: 'FIELD_ADMIN',
  AGENT: 'AGENT',
} as const;
export type UserType = (typeof UserType)[keyof typeof UserType];

/** Anitra staff — not bound to a client. */
export const PLATFORM_USER_TYPES: readonly UserType[] = [UserType.SUPER_ADMIN, UserType.ADMIN];

/** Client organisation's web user. */
export const CLIENT_USER_TYPES: readonly UserType[] = [UserType.CLIENT_ADMIN];

export const Permission = {
  DASHBOARD_VIEW: 'dashboard:view',
  ADMIN_USERS_READ: 'admin-users:read',
  ADMIN_USERS_MANAGE: 'admin-users:manage',
  CLIENTS_READ: 'clients:read',
  CLIENTS_MANAGE: 'clients:manage',
  FIELD_ADMINS_READ: 'field-admins:read',
  FIELD_ADMINS_MANAGE: 'field-admins:manage',
  AGENTS_READ: 'agents:read',
  AGENTS_MANAGE: 'agents:manage',
  ROLES_READ: 'roles:read',
  ROLES_MANAGE: 'roles:manage',
  LOGIN_LOGS_READ: 'login-logs:read',
  AUDIT_LOGS_READ: 'audit-logs:read',
  ML_LOGS_READ: 'ml-logs:read',
  FAQS_READ: 'faqs:read',
  FAQS_MANAGE: 'faqs:manage',
  MASTERS_READ: 'masters:read',
  MASTERS_MANAGE: 'masters:manage',
  FARMERS_READ: 'farmers:read',
  FARMERS_MANAGE: 'farmers:manage',
  ANIMALS_READ: 'animals:read',
  ANIMALS_MANAGE: 'animals:manage',
  ANIMALS_REVIEW: 'animals:review',
  ANIMAL_RECORDS_READ: 'animal-records:read',
  ANIMAL_RECORDS_MANAGE: 'animal-records:manage',
  GRIEVANCES_READ: 'grievances:read',
  GRIEVANCES_MANAGE: 'grievances:manage',
  NOTIFICATIONS_SEND: 'notifications:send',
  REPORTS_READ: 'reports:read',
  REPORTS_EXPORT: 'reports:export',
  SETTINGS_MANAGE: 'settings:manage',
} as const;
export type Permission = (typeof Permission)[keyof typeof Permission];

/** Which login the user chose on the sign-in page. The API rejects a user of the other portal. */
export const Portal = {
  PLATFORM: 'PLATFORM',
  CLIENT: 'CLIENT',
} as const;
export type Portal = (typeof Portal)[keyof typeof Portal];

export interface AuthUser {
  id: string;
  name: string;
  userType: UserType;
  /** Null for platform users. */
  clientId: string | null;
  /** Shown in the header ("Welcome to …") for client users. */
  clientName?: string;
  permissions: Permission[];
}

/** Response of `POST /auth/login` and `POST /auth/refresh`. */
export interface AuthSession {
  accessToken: string;
  /** Access-token lifetime in seconds. The refresh token is in an httpOnly cookie. */
  expiresIn: number;
  user: AuthUser;
}

export interface LoginRequest {
  portal: Portal;
  /** Mobile number or email. */
  identifier: string;
  password: string;
}
